/**
 * Shared TypeBox schemas for the Phase 5 approval surface.
 *
 * Single source of truth consumed by:
 *  - Hub Fastify decide/list/admin routes (Plan 05-08).
 *  - HubClient SDK endpoint declarations (`approvalsDecide`, `approvalsList`,
 *    `adminListApprovals`, `adminGetApproval` in `packages/hub-client/src/endpoints.ts`).
 *  - hub-proxy passthrough (Plan 05-10) and the `ApprovalCard` widget (Plan 05-11).
 *  - Admin SPA approvals tab (Plan 05-12).
 *
 * Requirements: APPR-01 (approval-bound tool execution), APPR-05 (decide endpoint),
 * APPR-06 (user-facing + admin listing surfaces).
 *
 * ADRs: COP-010 (approval lifecycle), COP-011 (hash-binding for replay safety),
 * COP-013 (decision audit trail), COP-023 (SSE part envelopes), COP-024 (admin
 * approvals review surface).
 *
 * Decisions implemented:
 *  - D-01: sha256 hex `payloadHash` is REQUIRED on the decide body — Hub returns
 *    409 Conflict when the persisted approval row's hash does not match
 *    (enforcement lives in Plan 05-08).
 *  - D-02: SSE part envelopes use the `data-approval-*` family
 *    (`data-approval-pending`, `data-approval-resolved`, `data-approval-error`).
 *  - D-04: decide returns 410 Gone when the approval has expired; row shape is
 *    reused by that response (status flipped to `expired`).
 *  - D-08: `edit` action carries the user's edited intent via `reason`
 *    (Edit textarea pre-filled from the approval row's `summary`).
 *  - D-10: `reject` action carries optional `reason` text bounded at 2000 chars
 *    to prevent log-pollution / DoS.
 *  - D-15: full decision-row shape is the durable audit trail surfaced through
 *    the admin show response (`adminGetApproval`).
 */
import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { ConsentScopeSchema } from './plans.js';
// COP-142 — the decide reply carries the same payload shape the live and reload
// seams already emit, so the widget applies ONE known shape from three sources.
import { TurnActionsPartDataSchema } from './chat.js';
/**
 * Lifecycle status of a stored approval row. `pending` is the initial state;
 * the decide route transitions to `approved` | `rejected` | `edited` on a
 * successful POST and to `expired` once `expiresAt` elapses with no decision.
 * (COP-010, D-04, D-08, D-10)
 */
export const ApprovalStatusSchema = Type.Union([
    Type.Literal('pending'),
    Type.Literal('approved'),
    Type.Literal('rejected'),
    Type.Literal('edited'),
    Type.Literal('expired'),
]);
/**
 * CR-01 split (Plan 05-15):
 *   - `ApprovalDecisionActionSchema` (4 members — `approve|reject|edit|expired`)
 *     is the audit-row READ shape. Used by `ApprovalDecisionRowSchema.action`
 *     and `ApprovalShowResponseSchema.decisions[].action`. The audit table
 *     accepts `'expired'` because the server-side adapter writes an audit row
 *     when an expiring approval is decided (COP-049 D-15 append-only timeline
 *     restored for the expiry transition).
 *   - `ApprovalDecideActionSchema` (3 members — `approve|reject|edit`) is the
 *     decide-body WRITE shape. Used by `ApprovalDecideBodySchema.action`.
 *     Clients posting to `POST /approvals/:id/decide` CANNOT post
 *     `action='expired'`; only the server-side adapter writes that action.
 *     This split preserves the wire-format gate at Fastify TypeBox entry.
 *
 * (D-08, D-10, D-15, CR-01; threat T-05-01 + T-05-CR01-SURFACE.)
 */
export const ApprovalDecisionActionSchema = Type.Union([
    Type.Literal('approve'),
    Type.Literal('reject'),
    Type.Literal('edit'),
    Type.Literal('expired'),
]);
export const ApprovalDecideActionSchema = Type.Union([
    Type.Literal('approve'),
    Type.Literal('reject'),
    Type.Literal('edit'),
]);
/**
 * D-01: 64-char lowercase hex sha256. Mismatch → 409 Conflict in Plan 05-08
 * decide route. Required-by-construction: every approval row carries one and
 * every decide body MUST echo it back unchanged.
 */
export const PayloadHashSchema = Type.String({
    minLength: 64,
    maxLength: 64,
    pattern: '^[a-f0-9]{64}$',
});
/**
 * Execution lifecycle of the approved action, distinct from the DECISION
 * lifecycle above (`ApprovalStatusSchema`). Mirrors the `tf_approval_execution_status`
 * Postgres enum 1:1 (`packages/hub/src/schema/agent.ts`). An approved row whose
 * execution never started stays `pending`; the crash-window sweep flips a lapsed
 * one to `failed` (COP-148 D7) and an indeterminate outbound call is `unknown`
 * (COP-138).
 *
 * COP-148 D8 — surfaced read-only on the admin queue so approved-never-ran,
 * `failed`, `unknown` and lapsed rows are findable. Visibility ONLY: COP-085's
 * read-only stance on the admin approvals surface is extended, not amended.
 */
export const ApprovalExecutionStatusSchema = Type.Union([
    Type.Literal('pending'),
    Type.Literal('executing'),
    Type.Literal('executed'),
    Type.Literal('failed'),
    Type.Literal('unknown'),
]);
/**
 * Drizzle-row JSON view of `tf_approval`. `payload` is opaque to the schema
 * (the unredacted bound payload is admin-only via `adminGetApproval`; widget
 * users only see the human-readable `summary`). The `summary` field carries the
 * confirmation message captured from the tool's
 * `x-trackframe-agent.confirmation.message` annotation — used by `ApprovalCard`
 * to pre-fill the Edit textarea per D-08. (COP-010, COP-011)
 */
export const ApprovalRowSchema = Type.Object({
    id: UuidSchema,
    sessionId: UuidSchema,
    userId: UuidSchema,
    trackId: Type.Union([UuidSchema, Type.Null()]),
    toolName: Type.String({ minLength: 1, maxLength: 200 }),
    toolCallId: Type.String({ minLength: 1, maxLength: 200 }),
    payload: Type.Unknown(),
    payloadHash: PayloadHashSchema,
    status: ApprovalStatusSchema,
    reason: Type.Union([Type.String({ maxLength: 2000 }), Type.Null()]),
    summary: Type.Union([Type.String({ maxLength: 2000 }), Type.Null()]),
    createdAt: IsoDateTimeSchema,
    // COP-160 P35 — `null` = the approval never expires.
    expiresAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    decidedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    // NO `executionStatus` / `executionError` HERE, DELIBERATELY. This is the
    // USER-facing wire row (`GET /approvals`, `GET /approvals/:id`, the decide
    // reply). COP-134 lists "putting `execution_result` / `execution_error` on the
    // wire" under Alternatives rejected, citing HUB-024, and COP-148 D8 authorises
    // the ADMIN queue only. The two fields live on `AdminApprovalRowSchema` below.
    // `wire-execution-fields.test.ts` (packages/hub/agent/approvals) is the gate.
});
/**
 * COP-148 D8 — the ADMIN-ONLY wire row: the user row plus the execution truth
 * the admin approvals queue renders. Declared as the response shape of
 * `GET /admin/approvals` and `GET /admin/approvals/:id`, and NOWHERE else.
 *
 * A composition, never a copy: every field the user row gains arrives here too,
 * so the two cannot drift.
 *
 * `executionStatus` is REQUIRED — the column is NOT NULL DEFAULT 'pending', so
 * an optional field would let a mapper that forgot the column look identical to
 * a row that legitimately has none. `executionError` is nullable-optional and,
 * since COP-148's task-06a amendment, always a SHORT TYPED CODE (≤64 chars,
 * `toExecutionErrorCode` at the write site) — never a raw driver or track
 * message, which routinely carries payload fragments (HUB-024).
 */
export const AdminApprovalRowSchema = Type.Composite([
    ApprovalRowSchema,
    Type.Object({
        executionStatus: ApprovalExecutionStatusSchema,
        executionError: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    }),
]);
/**
 * Drizzle-row JSON view of `tf_approval_decision` — the immutable audit log
 * entry written alongside the approval state transition. `payloadHash` is
 * captured at decision time to bind the audit trail to the exact bytes the
 * agent intended to execute. (COP-013, D-15)
 */
export const ApprovalDecisionRowSchema = Type.Object({
    id: UuidSchema,
    approvalId: UuidSchema,
    actor: UuidSchema,
    action: ApprovalDecisionActionSchema,
    at: IsoDateTimeSchema,
    payloadHash: PayloadHashSchema,
    reason: Type.Union([Type.String({ maxLength: 2000 }), Type.Null()]),
});
/** Path params shared by `POST /approvals/:id/decide` and `GET /admin/approvals/:id`. */
export const ApprovalIdParamsSchema = Type.Object({ id: UuidSchema });
/**
 * Decide request body. `payloadHash` is REQUIRED (D-01 hash-binding) — the
 * Fastify TypeBox provider rejects bodies missing the field at request entry.
 * `reason` is optional: carries Reject text per D-10 or Edit freeform-intent
 * per D-08.
 */
export const ApprovalDecideBodySchema = Type.Object({
    action: ApprovalDecideActionSchema,
    payloadHash: PayloadHashSchema,
    reason: Type.Optional(Type.String({ maxLength: 2000 })),
    // COP-093 §Consent card — the user's per-step scope choice. `per_turn` (or
    // absent) is the single-use approval path with no standing grant; `session`
    // or `always` mint a tool-level `tf_consent_grant` on `action='approve'`.
    // Scope is set ONLY from this explicit field, never inferred (COP-044).
    consentScope: Type.Optional(ConsentScopeSchema),
});
/**
 * COP-093 §Revocation — body for `POST /approvals/revoke-grant`, the inline
 * "you always allow this — Undo" control on the per-step consent card. Revokes
 * the caller's active `always` grant for `(trackId, toolName)` (RLS-scoped to
 * the owner). Idempotent: a second revoke is a no-op. This is a standing-consent
 * operation only — it does not touch any `tf_approval` row.
 */
export const ApprovalRevokeGrantBodySchema = Type.Object({
    trackId: UuidSchema,
    toolName: Type.String({ minLength: 1, maxLength: 200 }),
});
/** Response for `POST /approvals/revoke-grant`: whether an active grant was revoked. */
export const ApprovalRevokeGrantResponseSchema = Type.Object({
    revoked: Type.Boolean(),
});
/**
 * COP-094 §REST contract — query for `GET /approvals/grants`, the widget
 * standing-consent management dropdown (UI-06, APPR-10). The caller supplies
 * only `sessionId`; the hub derives the track from the session row (RLS-scoped
 * to the owner) and lists the user's active `always` grants for that track plus
 * `session` grants for that session with `plan_id IS NULL`. per-turn approvals
 * (no grant row) and plan-envelope grants never appear.
 */
export const ApprovalGrantsListQuerySchema = Type.Object({
    sessionId: UuidSchema,
});
/**
 * A single standing consent grant row in the management dropdown (COP-094,
 * APPR-10). Exposes only what the dropdown renders — never `payload_hash`,
 * `plan_id`, or other internal binding state.
 */
export const ConsentGrantRowSchema = Type.Object({
    id: UuidSchema,
    toolName: Type.String({ minLength: 1, maxLength: 200 }),
    scope: Type.Union([Type.Literal('session'), Type.Literal('always')]),
    createdAt: IsoDateTimeSchema,
});
/** Response for `GET /approvals/grants`: the caller's active standing grants. */
export const ApprovalGrantsListResponseSchema = Type.Object({
    grants: Type.Array(ConsentGrantRowSchema),
});
/** Params for `POST /approvals/grants/:id/revoke` — the grant id to revoke (COP-094). */
export const ConsentGrantIdParamsSchema = Type.Object({ id: UuidSchema });
/**
 * Decide success response. Returns the post-transition approval row AND the
 * freshly persisted decision row so callers (the widget) can advance their
 * local state without a second roundtrip. The continuation-turn re-attach
 * (D-13) is then driven by `useCopilot().sendMessage({ pendingActionId })`.
 *
 * COP-142 — plus `turnActions`, the turn's action list AFTER the decide.
 *
 * `decide` already settles the ledger in the same transaction as the status
 * transition (COP-135 D1/TACT-01), so the correct list exists the instant the
 * route answers. What was missing is a way to SAY so: the route holds no SSE
 * writer (COP-135 D3), and only the `approve` arm continues the turn, so a
 * reject/edit left the on-screen list reading "Waiting for you" beside a card
 * already reading "Rejected" — two statements about one action, on one screen,
 * disagreeing until a reload.
 *
 * OPTIONAL, and absent rather than empty when there is nothing to say — an
 * approval minted before the ledger existed settles no row, and `actions: []`
 * would assert the turn did nothing (COP-135's absent-vs-empty rule).
 */
export const ApprovalDecideResponseSchema = Type.Object({
    approval: ApprovalRowSchema,
    decision: ApprovalDecisionRowSchema,
    turnActions: Type.Optional(TurnActionsPartDataSchema),
    // COP-160 P37/P57 — present only for `consentScope: 'schedule'`: `true` when
    // the decide appended the tool to the schedule's allowlist, `false` when the
    // tool was already on it.
    widened: Type.Optional(Type.Boolean()),
});
/**
 * Query for `GET /approvals` (user-scoped via RLS, Plan 05-08) and
 * `GET /admin/approvals` (admin-gated). `limit` is capped at 200 to bound
 * response size; `offset` enables stateless paging. (COP-024)
 */
export const ApprovalListQuerySchema = Type.Object({
    status: Type.Optional(ApprovalStatusSchema),
    // COP-148 D8 — exact match, ANDed with every other filter. This is what makes
    // an approved-never-ran row (`status='approved'` + `executionStatus='pending'`)
    // findable at all.
    executionStatus: Type.Optional(ApprovalExecutionStatusSchema),
    trackId: Type.Optional(UuidSchema),
    userId: Type.Optional(UuidSchema),
    fromCreatedAt: Type.Optional(IsoDateTimeSchema),
    toCreatedAt: Type.Optional(IsoDateTimeSchema),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
/** USER-facing list response (`GET /approvals`). Carries no execution state. */
export const ApprovalListResponseSchema = Type.Object({
    approvals: Type.Array(ApprovalRowSchema),
    total: Type.Integer({ minimum: 0 }),
});
/**
 * COP-148 D8 — ADMIN list response (`GET /admin/approvals`). Same envelope, the
 * admin row. Separate from {@link ApprovalListResponseSchema} because the two
 * routes share `ApprovalListQuerySchema` but must NOT share the row: the filter
 * is a query parameter and reveals nothing, the row is the disclosure surface.
 */
export const AdminApprovalListResponseSchema = Type.Object({
    approvals: Type.Array(AdminApprovalRowSchema),
    total: Type.Integer({ minimum: 0 }),
});
/**
 * Query for `GET /admin/approvals/:id` — the admin Show route requires the
 * target user's id as a defense-in-depth match (Plan 05-08; adapter asserts
 * `row.userId === targetUserId`). Plan 05-12's admin SPA list view forwards
 * `row.userId` to the Show click-through URL so the typed SDK call carries
 * this query param. Single source of truth for both hub route + SDK endpoint
 * declaration. (APPR-07, COP-024, HUB-024 §6)
 */
export const ApprovalShowQuerySchema = Type.Object({
    userId: UuidSchema,
});
/**
 * Admin show response — approval row + full chronological decisions[] audit
 * trail. Drives the admin SPA approvals detail view (Plan 05-12). (D-15)
 *
 * ADMIN-ONLY, despite the un-prefixed name: `GET /admin/approvals/:id` is its
 * one and only declaring route, so it carries {@link AdminApprovalRowSchema}
 * (COP-148 D8). No user-facing route may declare it.
 */
export const ApprovalShowResponseSchema = Type.Object({
    approval: AdminApprovalRowSchema,
    decisions: Type.Array(ApprovalDecisionRowSchema),
});
/**
 * SSE part envelope emitted on the chat stream when the agent enters the
 * approval gate. (D-02 `data-approval-pending`). `args` is opaque — the bound
 * payload is redacted out-of-band per D-17 before any persistence/log surface.
 */
export const ApprovalPendingPartSchema = Type.Object({
    approvalId: UuidSchema,
    toolName: Type.String({ minLength: 1, maxLength: 200 }),
    toolCallId: Type.String({ minLength: 1, maxLength: 200 }),
    args: Type.Unknown(),
    payloadHash: PayloadHashSchema,
    // COP-160 P35 — `null` = the approval never expires (no expiry line).
    expiresAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    summary: Type.Optional(Type.String({ maxLength: 2000 })),
    // COP-093 — the track this write is scoped to. The widget gates the
    // "Always allow <tool>" consent choice on it (an always grant is keyed on
    // `(owner, track, tool)`); `null` when the turn has no track context, which
    // correctly suppresses the always-on button.
    trackId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
    // COP-160 SCHED-20a — the approval parks a SCHEDULED execution: the widget
    // offers "Approve once" and "Approve and allow for this schedule".
    scheduledExecution: Type.Optional(Type.Boolean()),
});
/**
 * SSE part envelope emitted by the resumed agent turn after the user decides.
 * (D-02 `data-approval-resolved`). `reason` carries Reject text (D-10) or Edit
 * freeform-intent (D-08); absent for plain `approve`.
 */
export const ApprovalResolvedPartSchema = Type.Object({
    approvalId: UuidSchema,
    action: ApprovalDecisionActionSchema,
    reason: Type.Optional(Type.String({ maxLength: 2000 })),
    /**
     * ATRU-01 / COP-134 D1 — EXECUTION truth alongside the decision, computed
     * from `tf_approval.execution_status` (never from model text). Only the
     * TERMINAL values reach the wire.
     *
     * ABSENT means "no execution state to show" — a rejected/edited/expired
     * approval, or an approved one whose execution has not been attempted or is
     * still running. Absent is NOT "failed".
     *
     * `unknown` (COP-138) is terminal and distinct from both: the call was
     * dispatched and its answer was lost, so the card must ask the user to check
     * rather than assert either way. Mapping it onto `failed` — or letting it
     * fall through to ABSENT, which reads as "nothing happened" — both hand the
     * user a confident wrong answer.
     *
     * The raw `execution_result` / `execution_error` deliberately stay OFF the
     * USER-facing wire (HUB-024, COP-134 Alternatives rejected): the chip is a
     * status, not a dump — a user who needs detail has the tool-output part,
     * which already ships. COP-148 D8 puts `execution_error` on the ADMIN queue
     * alone (`AdminApprovalRowSchema`), and only as a short typed code.
     */
    execution: Type.Optional(Type.Union([
        Type.Literal('executed'),
        Type.Literal('failed'),
        Type.Literal('unknown'),
    ])),
});
/**
 * SSE part envelope for soft-failure / replay-rejection paths. (D-02
 * `data-approval-error`.) `code` is a closed union so the widget can branch
 * on it without parsing `message`. `message` is human-readable, bounded to
 * 500 chars.
 */
export const ApprovalErrorPartSchema = Type.Object({
    approvalId: UuidSchema,
    code: Type.Union([
        Type.Literal('hash_mismatch'),
        Type.Literal('expired'),
        Type.Literal('not_found'),
        Type.Literal('denied'),
        Type.Literal('persist_failed'),
        // The action was approved and ran, and the run failed — not a refusal.
        Type.Literal('execution_failed'),
    ]),
    message: Type.String({ maxLength: 500 }),
});
/**
 * M4 — typed-per-status HTTP error envelope for `POST /approvals/:id/decide`
 * 409 Conflict responses (D-01 hash-mismatch + T-05-55 already-decided replay).
 *
 * Why a dedicated schema (instead of reusing the narrow shared
 * `ErrorResponseSchema = { error, message }`): Fastify's TypeBox response
 * serializer strips fields that aren't declared in the response schema for the
 * matching status code. The 409 envelope carries `code` so the widget can
 * branch on `'hash_mismatch'` vs `'already_decided'` without parsing
 * `message`. Declaring this schema as the 409 response keeps `code` round-
 * tripping intact. (M4 review concern.)
 */
const ApprovalConflictCodeSchema = Type.Union([
    Type.Literal('hash_mismatch'),
    Type.Literal('already_decided'),
    Type.Literal('allowlist_full'),
    Type.Literal('schedule_deleted'),
]);
export const ApprovalConflictResponseSchema = Type.Object({
    // COP-160 P37 — `allowlist_full`: "Approve and allow for this schedule" on a
    // schedule that already allows 100 tools; the approval stays `pending`.
    // COP-160 P81 — `schedule_deleted`: an approve on a scheduled run whose
    // schedule was deleted; nothing is written, the approval stays `pending`.
    error: ApprovalConflictCodeSchema,
    message: Type.String(),
    code: ApprovalConflictCodeSchema,
});
/**
 * M4 — typed-per-status HTTP error envelope for `POST /approvals/:id/decide`
 * 410 Gone responses (D-04 expired-row sentinel).
 *
 * Carries `expiresAt` so the widget can render the original expiry instant
 * (and `code: 'expired'` for closed-union branching). Same rationale as
 * `ApprovalConflictResponseSchema` — Fastify's serializer would strip both
 * fields if the route declared the narrow `ErrorResponseSchema` for 410.
 * (M4 review concern; threat T-05-75 — no payload / no tool name leakage.)
 */
export const ApprovalExpiredResponseSchema = Type.Object({
    error: Type.Literal('expired'),
    code: Type.Literal('expired'),
    message: Type.String(),
    expiresAt: IsoDateTimeSchema,
});
//# sourceMappingURL=approvals.js.map